// Sektör paketleri §5 "Katalog" satırı (DR15/DR17) — katalog tablosunun paket süzgeci ve rozetleri
// (saf; jsdom yok). Bileşenler: CatalogTable, CatalogPackageBar. Seçim kutusu + "Pakete ekle"
// 2026-11-03'te kaldırıldı (ürün kararı — paket düzenleyicisi tek yol).
// Üyelik `kpi-packages-store` `packageMembership` (metricId → paket kimlikleri) üzerinden okunur.

import type { KpiMetric, KpiPackage } from "@mahir/shared";
import type { IndicatorTree, TreeRow } from "./kpi-set-editor";

/** Rozet kısa adı: paket adının ilk 12 karakteri (tam ad `title`/popover'da). */
export const PACKAGE_BADGE_CHARS = 12;
/** Satırda gösterilen en çok rozet; fazlası "+N". */
export const PACKAGE_BADGE_MAX = 2;

export function shortPackageName(name: string): string {
  return name.trim().slice(0, PACKAGE_BADGE_CHARS);
}

export interface PackageBadges {
  shown: { id: string; name: string; short: string }[];
  /** "+N" — gösterilmeyen paket sayısı */
  rest: number;
  /** popover'daki tam liste (gösterilenler dahil) */
  all: { id: string; name: string }[];
}

/**
 * Satır rozetleri: yalnız adı bilinen (etkin) paketler; süzgeç açıkken yalnız süzülen paketler.
 * Sıra: paket listesinin (`namesById` ekleme) sırası.
 */
export function packageBadgesFor(
  metricId: string,
  membership: Readonly<Record<string, readonly string[]>>,
  namesById: ReadonlyMap<string, string>,
  filterIds: readonly string[],
): PackageBadges {
  const mine = new Set(membership[metricId] ?? []);
  const filter = filterIds.length > 0 ? new Set(filterIds) : null;
  const all: { id: string; name: string }[] = [];
  for (const [id, name] of namesById) {
    if (!mine.has(id) || (filter && !filter.has(id))) continue;
    all.push({ id, name });
  }
  const shown = all
    .slice(0, PACKAGE_BADGE_MAX)
    .map((p) => ({ ...p, short: shortPackageName(p.name) }));
  return { shown, rest: Math.max(0, all.length - shown.length), all };
}

/** Etkin paketler → ad haritası (rozet + süzgeç seçenekleri tek kaynaktan). */
export function activePackageNames(packages: readonly KpiPackage[]): Map<string, string> {
  return new Map(packages.filter((p) => p.status === "active").map((p) => [p.id, p.name]));
}

/**
 * Süzgeç görünümü: seçili paketlerden en az birinin üyesi olan göstergeler + ataları.
 * `filterIds` boşsa null (süzgeç yok). Sayım özeti (DR17): görünür / toplam.
 */
export function packageFilterVisible(
  metrics: readonly Pick<KpiMetric, "id" | "parentId">[],
  membership: Readonly<Record<string, readonly string[]>>,
  filterIds: readonly string[],
): Set<string> | null {
  if (filterIds.length === 0) return null;
  const filter = new Set(filterIds);
  const parentOf = new Map(metrics.map((m) => [m.id, m.parentId]));
  const out = new Set<string>();
  for (const m of metrics) {
    if (!(membership[m.id] ?? []).some((id) => filter.has(id))) continue;
    let cur: string | null | undefined = m.id;
    while (cur && !out.has(cur)) {
      out.add(cur);
      cur = parentOf.get(cur) ?? null;
    }
  }
  return out;
}

export interface CatalogRowsFilter {
  rootId: string | null;
  /** arama görünürlüğü (`visibleIdsForSearch`); null = arama yok */
  searchVisible: ReadonlySet<string> | null;
  /** paket süzgeci görünürlüğü; null = süzgeç yok */
  packageVisible: ReadonlySet<string> | null;
  /** kapalı düğümler (süzgeç/arama açıkken yok sayılır — her şey açık gösterilir) */
  collapsed: ReadonlySet<string>;
}

/** Katalog tablosunun gösterdiği satırlar (kök sekmesi + arama + paket süzgeci + aç/kapa). */
export function filterCatalogRows<T extends KpiMetric>(
  tree: IndicatorTree<T>,
  f: CatalogRowsFilter,
): TreeRow<T>[] {
  const filtering = f.searchVisible !== null || f.packageVisible !== null;
  const out: TreeRow<T>[] = [];
  const hiddenUnder = new Set<string>();
  for (const r of tree.rows) {
    if (f.rootId && r.rootId !== f.rootId) continue;
    if (f.searchVisible && !f.searchVisible.has(r.node.id)) continue;
    if (f.packageVisible && !f.packageVisible.has(r.node.id)) continue;
    if (!filtering && r.node.parentId && hiddenUnder.has(r.node.parentId)) {
      hiddenUnder.add(r.node.id);
      continue;
    }
    out.push(r);
    if (!filtering && r.hasChildren && f.collapsed.has(r.node.id)) hiddenUnder.add(r.node.id);
  }
  return out;
}
